import { inject, Injectable } from '@angular/core';
import { ServiceTypeMetadataMap } from '@common/constants';
import { TaskManagerOdata } from '@common/models';
import { OperationTypeToDisplayNamePipe, ServiceTypeToDisplayNamePipe } from '@common/pipes';
import { TaskManagerService } from '@common/services';
import { getUniqueListByProp, TaskManagerTag, TaskManagerTagsMap } from '@common/services/smart-search';
import SmartSearchTemplatesBase from '@common/services/smart-search/smart-search-template-base';
import { FilterOptions } from '@common/types';
import { getFilterByContains, getFilterByEq, getFilterByEqFromEnum } from '@common/utils/functions/search';
import { ModelTemplate } from 'mbs-ui-kit';
import { asapScheduler, Observable, scheduled } from 'rxjs';
import { map, tap } from 'rxjs/operators';

@Injectable()
export class SmartSearchModelTemplateTaskManagerService extends SmartSearchTemplatesBase {
  private readonly taskManagerService = inject(TaskManagerService);
  private readonly taskManagerService$ = this.taskManagerService.getTasks();

  readonly #serviceType = TaskManagerTagsMap.get(TaskManagerTag.ServiceType);
  readonly #status = TaskManagerTagsMap.get(TaskManagerTag.Status);
  readonly #task = TaskManagerTagsMap.get(TaskManagerTag.Task);
  readonly #userName = TaskManagerTagsMap.get(TaskManagerTag.UserName);

  private keyCache = 'task-manger-name_';

  private readonly serviceTypeToDisplayNamePipe = new ServiceTypeToDisplayNamePipe();
  private readonly operationTypeToDisplayNamePipe = new OperationTypeToDisplayNamePipe();

  public readonly UserNameTag: ModelTemplate<TaskManagerOdata> = {
    tag: this.#userName.tag,
    items: (state): Observable<TaskManagerOdata[]> => {
      const term = state.leftCaretValue;
      const options: FilterOptions = {
        model: [{ value: term }],
        prop: this.#userName.prop
      };

      this.taskManagerService.filter = term ? getFilterByContains(options) : '';

      // if (this.cache[this.keyCache + term]) {
      //   return of(this.cache[this.keyCache + term]) as Observable<TaskManagerOdata[]>;
      // }

      return scheduled(
        this.taskManagerService$.pipe(
          map((res) => res.data),
          map((tasks) => {
            const filtered = tasks.filter((task) => task.UserName.toLowerCase().includes(term));

            return getUniqueListByProp(filtered, this.#userName.prop).slice(0, 10);
          }),
          tap(this.writeToCache(this.keyCache + term))
        ),
        asapScheduler
      ) as Observable<TaskManagerOdata[]>;
    },
    itemFormatter: ({ UserName }): string => UserName,
    addGroupBrackets: true
  };

  public readonly ServiceTypeTag: ModelTemplate<TaskManagerOdata> = {
    tag: this.#serviceType.tag,
    items: (state): Observable<TaskManagerOdata[]> => {
      const term = state.leftCaretValue;
      const options: FilterOptions = {
        model: [{ value: term }],
        prop: this.#serviceType.prop,
        enumItems: ServiceTypeMetadataMap
      };

      this.taskManagerService.filter = term ? getFilterByEqFromEnum(options) : '';

      // if (this.cache[this.keyCache + term]) {
      //   return of(this.cache[this.keyCache + term]) as Observable<TaskManagerOdata[]>;
      // }

      return scheduled(
        this.taskManagerService$.pipe(
          map((res) => res.data),
          map((tasks) => {
            const filtered = tasks.filter((task) => task.ServiceType.toLowerCase().includes(term));

            return getUniqueListByProp(filtered, this.#serviceType.prop).slice(0, 10);
          }),
          tap(this.writeToCache(this.keyCache + term))
        ),
        asapScheduler
      ) as Observable<TaskManagerOdata[]>;
    },
    itemFormatter: ({ ServiceType }): string => this.serviceTypeToDisplayNamePipe.transform(ServiceType),
    addGroupBrackets: true
  };

  public readonly TaskTag: ModelTemplate<TaskManagerOdata> = {
    tag: this.#task.tag,
    items: (state): Observable<TaskManagerOdata[]> => {
      const term = state.leftCaretValue;
      const options: FilterOptions = {
        model: [{ value: term }],
        prop: this.#task.prop
      };

      this.taskManagerService.filter = term ? getFilterByEq(options) : '';

      // if (this.cache[this.keyCache + term]) {
      //   return of(this.cache[this.keyCache + term]) as Observable<TaskManagerOdata[]>;
      // }

      return scheduled(
        this.taskManagerService$.pipe(
          map((res) => res.data),
          map((tasks) => {
            const filtered = tasks.filter((task) => task.OperationType.toLowerCase().includes(term));

            return getUniqueListByProp(filtered, this.#task.prop).slice(0, 10);
          }),
          tap(this.writeToCache(this.keyCache + term))
        ),
        asapScheduler
      ) as Observable<TaskManagerOdata[]>;
    },
    itemFormatter: ({ OperationType }): string => this.operationTypeToDisplayNamePipe.transform(OperationType),
    addGroupBrackets: true
  };

  public readonly StatusTag: ModelTemplate<TaskManagerOdata> = {
    tag: this.#status.tag,
    items: (state): Observable<TaskManagerOdata[]> => {
      const term = state.leftCaretValue;
      const options: FilterOptions = {
        model: [{ value: term }],
        prop: this.#status.prop
      };

      this.taskManagerService.filter = term ? getFilterByEq(options) : '';

      // if (this.cache[this.keyCache + term]) {
      //   return of(this.cache[this.keyCache + term]) as Observable<TaskManagerOdata[]>;
      // }

      return scheduled(
        this.taskManagerService$.pipe(
          map((res) => res.data),
          map((tasks) => {
            const filtered = tasks.filter((task) => task.State.toLowerCase().includes(term));

            return getUniqueListByProp(filtered, this.#status.prop).slice(0, 10);
          }),
          tap(this.writeToCache(this.keyCache + term))
        ),
        asapScheduler
      ) as Observable<TaskManagerOdata[]>;
    },
    itemFormatter: ({ State }): string => State,
    addGroupBrackets: true
  };
}
